import { platform, portal } from './platform';
import { settings } from './settings';
import { track } from './state/telemetry';

/**
 * Fullscreen for phone browsers. In landscape a mobile browser's toolbars take up to a third of the
 * screen height; Android Chrome can hide them (Fullscreen API) and lock landscape while fullscreen.
 * iPhone Safari has no fullscreen for pages: there the PWA (Add to Home Screen) is the way out.
 * Requests must run inside a tap handler (user activation).
 */

type OrientationLock = ScreenOrientation & { lock?: (o: 'landscape') => Promise<void> };

const coarse = (): boolean => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

/** Installed web app (home-screen icon): already fullscreen. */
export const isStandalone = (): boolean =>
  (typeof matchMedia === 'function' && matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches) ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** A touch-screen browser tab (not the native shells, not desktop, not the installed web app). */
export const isMobileBrowser = (): boolean => platform === 'web' && coarse() && !isStandalone();

export function canFullscreen(): boolean {
  // Portals provide fullscreen themselves and forbid custom buttons (they interfere with the portal's ads).
  return !portal && isMobileBrowser() && !!document.fullscreenEnabled && typeof document.documentElement.requestFullscreen === 'function';
}

/** iPhone/iPad Safari tab: no page fullscreen, but Add to Home Screen gives a full-screen app. */
export function isIosBrowser(): boolean {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && isMobileBrowser();
}

/** Goes fullscreen and locks landscape. Resolves false when the browser refuses. */
export async function enterFullscreen(): Promise<boolean> {
  if (!canFullscreen()) return false;
  try {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
  } catch {
    track('fullscreen', { label: 'refused' });
    return false;
  }
  track('fullscreen', { label: 'on' });
  try {
    await (screen.orientation as OrientationLock | undefined)?.lock?.('landscape');
  } catch {
    /* lock is optional (unsupported or refused) */
  }
  return true;
}

/** Called from Play taps: phone browsers go fullscreen for matches unless the player turned it off. */
export function fullscreenForPlay(): void {
  if (settings.value.autoFullscreen && canFullscreen() && !document.fullscreenElement) void enterFullscreen();
}
